// @flow
import React, { useContext } from 'react';
import { Grid } from 'semantic-ui-react';
import { getIn, useFormikContext } from 'formik';
import { isEqual } from 'lodash';

import { PaddedAndRaisedSegment } from '../presentational/PaddedAndRaisedSegment';
import { SaveFormButton } from '../presentational/buttons/SaveFormButton';
import { ResetFormButton } from '../presentational/buttons/ResetFormButton';
import { Spacer } from '../presentational/spacing/Spacer';
import { If } from '../presentational/If';
import { toastError } from '../presentational/notifications/utils';
import { useIsUserAdmin } from '../../custom-hooks/useIsUserAdmin';
import { EstimateContext } from '../../contexts/EstimateContext';
import { useUpdateAdvancedSecurityConfigurationMutation } from '../../service-definitions/estimates';
import {
  ADVANCED_SECURITY_CUSTOM_DISCOUNTS_JSON_BACKEND_NAME,
  ADVANCED_SECURITY_TIER_CONFIG_BACKEND_NAME,
  ESTIMATE_ADVANCED_SECURITY_DETAILS_FORM,
} from '../../constants';
import { FixedRowsGrid } from '../../formik-utils/FixedRowsGrid';
import { ADVANCED_SECURITY_TIERS_COLUMNS_CONFIGURATION } from '../../configuration/advanced-security';
import { isAnythingEditedOnEstimatePageBesidesTheGivenForm } from '../views/utils';

import { AdvancedSecurityConfigureButton } from './AdvancedSecurityConfigureButton';
import { ADVANCED_SECURITY_TIERS_PATH } from './constants';
import { buildAdvancedSecurityTierConfigPayload } from './utils';

export const AdvancedSecurityCustomDiscountsContainer = () => {
  const { values, initialValues, setFieldValue, errors } = useFormikContext();
  const {
    inputs: { id: estimateId, estimate_version: estimateVersion },
  } = useContext(EstimateContext);
  const [updateAdvancedSecurityConfiguration] = useUpdateAdvancedSecurityConfigurationMutation();
  const isUserAdmin = useIsUserAdmin();
  const readOnly = !isUserAdmin;

  const hasTiers = getIn(values, ADVANCED_SECURITY_TIERS_PATH) != null;

  const hasAdvancedSecurityValidationErrors =
    getIn(errors, ADVANCED_SECURITY_CUSTOM_DISCOUNTS_JSON_BACKEND_NAME) != null;

  const isAdvancedSecurityEdited = !isEqual(
    getIn(values, ADVANCED_SECURITY_CUSTOM_DISCOUNTS_JSON_BACKEND_NAME),
    getIn(initialValues, ADVANCED_SECURITY_CUSTOM_DISCOUNTS_JSON_BACKEND_NAME)
  );

  const shouldFormBeDisabled = isAnythingEditedOnEstimatePageBesidesTheGivenForm(
    values,
    initialValues,
    ESTIMATE_ADVANCED_SECURITY_DETAILS_FORM
  );

  const handleSave = async () => {
    const tiers = getIn(values, ADVANCED_SECURITY_TIERS_PATH);
    const { error } = await updateAdvancedSecurityConfiguration({
      estimateId,
      payload: {
        estimate_version: estimateVersion,
        [ADVANCED_SECURITY_TIER_CONFIG_BACKEND_NAME]: buildAdvancedSecurityTierConfigPayload(tiers),
      },
    });
    if (error) {
      toastError(error);
    }
  };

  const handleCancel = () => {
    setFieldValue(
      ADVANCED_SECURITY_CUSTOM_DISCOUNTS_JSON_BACKEND_NAME,
      initialValues?.[ADVANCED_SECURITY_CUSTOM_DISCOUNTS_JSON_BACKEND_NAME] ?? {}
    );
  };

  return (
    <PaddedAndRaisedSegment disabled={shouldFormBeDisabled}>
      <If test={!hasTiers}>
        <AdvancedSecurityConfigureButton disabled={readOnly} />
      </If>
      <If test={hasTiers}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={12}>
              <FixedRowsGrid
                columnsConfiguration={ADVANCED_SECURITY_TIERS_COLUMNS_CONFIGURATION}
                pathToFollowInValues={ADVANCED_SECURITY_TIERS_PATH}
                readOnly={readOnly}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <If test={isUserAdmin}>
          <Spacer y={20} />
          <ResetFormButton disabled={!isAdvancedSecurityEdited} onClick={handleCancel} />
          <Spacer x={5} />
          <SaveFormButton
            disabled={!isAdvancedSecurityEdited || hasAdvancedSecurityValidationErrors}
            onClick={handleSave}
          />
        </If>
      </If>
    </PaddedAndRaisedSegment>
  );
};
